<script setup lang="ts">
// Faixa de ROLLOVER do bônus no header do jogo — DESKTOP (Figma 24166:44189).
// Mesmo papel que InGameRollover.vue (mobile): substitui a faixa de XP
// (InGameProgressDesktop.vue) enquanto a sessão roda na carteira de bônus.
// Aqui é uma PILULA inteira (raio total, "Union" 1B1F27), não a chapa de
// cantos de 5px do mobile — o desenho desktop não tem o degrau de baixo (sem
// chips, sem contador de XP), só a pilula com [rótulo] · [trilho] · [%].
//
// Valores por prop, exemplo do Figma como default — quem passa o dado real
// (usePlayWithBonus().rolloverAmount) é o consumidor
// (pages/games/[provider]/[game].vue). Quem decide SE esta faixa aparece (no
// lugar de LevelsInGameProgressDesktop) é o `isPlayingWithBonus` do `casino`
// store, publicado pelo GameIframe.
type Props = {
  amountBet?: number;
  amountTarget?: number;
};

const props = withDefaults(defineProps<Props>(), {
  amountBet: 940,
  amountTarget: 4000,
});

// Cálculo de %/rótulo é o mesmo da versão mobile (InGameRollover.vue) — só o
// template/CSS diverge entre as duas, por isso mora num composable.
const { progress, progressLabel, amountLabel } = useRolloverProgress(props);
</script>

<template>
  <div
    aria-label="Rollover saldo bônus"
    class="plate"
    role="img"
  >
    <span class="label">Rollover saldo bônus</span>

    <span class="track">
      <span
        class="track__fill"
        :style="{ width: `${progress}%` }"
      ></span>

      <!-- Centralizado no trilho INTEIRO, não só no preenchido — mesma regra
           do mobile (InGameRollover.vue), pra não fugir pra esquerda com
           progresso baixo. -->
      <span class="track__label">{{ amountLabel }}</span>
    </span>

    <span class="percent">{{ progressLabel }}%</span>
  </div>
</template>

<style scoped lang="postcss">
.plate {
  @apply flex w-full items-center;

  height: 44.654px;
  padding: 0 13px 0 18px;
  gap: 16px;
  border-radius: 999px;
  background: #1b1f27;
}

.label {
  @apply shrink-0 whitespace-nowrap text-white;

  font-size: 18px;
  font-weight: 600;
  line-height: 1;
}

.track {
  @apply relative block min-w-0 flex-1 overflow-hidden;

  height: 30px;
  border-width: 1.79px;
  border-style: solid;
  border-color: rgba(212, 253, 125, 0.4);
  border-radius: 999px;
  background: #001965;
  box-shadow: inset 0 3.265px 6.531px rgba(0, 0, 0, 0.5);
}

.track__fill {
  @apply absolute block rounded-full;

  /* Respiro entre o preenchimento e a borda do trilho (Figma: fill com inset
     em vez de ir de borda a borda). Direto no filho, e não via `padding` no
     `.track`: pra um elemento `position:absolute`, o container de referência
     já É a padding box do pai — o padding do pai não reposiciona um filho
     `inset`/`top`/`left`, então só entra aqui mesmo. */
  top: 2px;
  bottom: 2px;
  left: 2px;
  background: linear-gradient(90deg, #34a853, #84cc16);
  box-shadow: 0 0 26px rgba(163, 43, 255, 0.9);
}

.track__label {
  @apply absolute flex items-center justify-center overflow-hidden whitespace-nowrap px-2 text-white;

  top: 2px;
  right: 2px;
  bottom: 2px;
  left: 2px;
  font-size: 19px;
  font-weight: 700;
  line-height: 1;
}

.percent {
  @apply shrink-0 text-white tabular-nums;

  font-size: 18px;
  font-weight: 700;
  line-height: 1;
}
</style>
